import * as formatParse from 'common/format-parse';
import { RoundingMethod } from 'typings/enums';
import { RoundingStrategy, DefaultRoundingStrategy } from 'nomst/Stores/Utility/roundingStrategy';

export class Format {

    private _annualRoundingStrategy: RoundingStrategy;
    private _hourlyRoundingStrategy: RoundingStrategy;
    private _percentageRoundingStrategy: RoundingStrategy;
    private _stockRoundingStrategy: RoundingStrategy;

    constructor(
        annualRoundingStrategy: Nullable<RoundingStrategy>,
        hourlyRoundingStrategy: Nullable<RoundingStrategy>,
        percentageRoundingStrategy: Nullable<RoundingStrategy>,
        stockRoundingStrategy: Nullable<RoundingStrategy>) {
        this._annualRoundingStrategy = annualRoundingStrategy ?? DefaultRoundingStrategy;
        this._hourlyRoundingStrategy = hourlyRoundingStrategy ?? DefaultRoundingStrategy;
        this._percentageRoundingStrategy = percentageRoundingStrategy ?? DefaultRoundingStrategy;
        this._stockRoundingStrategy = stockRoundingStrategy ?? DefaultRoundingStrategy;
    }

    formatDecimalToPercentage(value: number): string {
        const percentage = value * 100;
        return this.formatPercentage(percentage);
    }

    formatPercentage(percentage: number): string {
        const roundedPercentageRatio = percentage / 100;
        return formatParse.formatPercentage(
            roundedPercentageRatio,
            this._percentageRoundingStrategy.decimalPrecision,
            this._percentageRoundingStrategy.decimalPrecision);
    }

    formatStockDecimal(value: number): string {
        return this.formatDecimal(value, this._stockRoundingStrategy);
    }

    formatDecimalValue(value: number, isHourly: boolean): string {
        const roundingStrategy = isHourly ? this._hourlyRoundingStrategy : this._annualRoundingStrategy;
        return this.formatDecimal(value, roundingStrategy);
    }

    formatCompaRatioToAbsolute(value: number): string {
        const roundingStrategy = new RoundingStrategy(RoundingMethod.Absolute, 2);
        return this.formatDecimal(value, roundingStrategy);
    }

    formatCurrencyValue(currency: Nullable<string>, value: number, isHourly: boolean): string {
        const roundingStrategy = isHourly ? this._hourlyRoundingStrategy : this._annualRoundingStrategy;
        if (!currency) {
            return this.formatDecimal(value, roundingStrategy);
        }

        return this.formatCurrency(
            currency,
            value,
            roundingStrategy);
    }

    formatCurrencyValueAbbreviated(currency: string, value: number, isHourly: boolean = false): string {
        const roundingStrategy = isHourly ? this._hourlyRoundingStrategy : this._annualRoundingStrategy;
        return formatParse.formatCurrencyValueAbbreviated(
            currency,
            value,
            roundingStrategy.decimalPrecision,
            roundingStrategy.decimalPrecision);
    }

    private formatDecimal(value: number, roundingStrategy: RoundingStrategy): string {
        return formatParse.formatDecimal(
            value,
            roundingStrategy.decimalPrecision,
            roundingStrategy.decimalPrecision);
    }

    private formatCurrency(currency: string, value: number, roundingStrategy: RoundingStrategy): string {
        return formatParse.formatCurrencyValue(
            currency,
            value,
            roundingStrategy.decimalPrecision,
            roundingStrategy.decimalPrecision);
    }
}